import React, { useState, useCallback, useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import {
  ArrowRight, Star, Quote, Calendar as CalendarIcon, Award, Users, Briefcase,
  Building2, TrendingUp, Search as SearchIcon, ChevronLeft, ChevronRight,
  MapPin, Clock, MessageCircle, Phone, Play, ExternalLink,
} from "lucide-react";

/* ------------------------------------------------------------ CASE STUDIES */
export const CASES = {
  "fashion-01": {
    name: "Nova Atelier", industry: "Fashion & E-commerce",
    hero: "Rebuilt a boutique fashion label into a nationally-recognised e-commerce brand.",
    timeline: "8 weeks · Nov 2025 – Jan 2026",
    services: ["Website Design", "Branding", "Photography", "SEO Optimization", "Digital Marketing"],
    tech: ["Next.js", "Shopify", "Klaviyo", "Meta Ads", "Google Analytics 4"],
    results: [
      { label: "Online revenue", before: "$4k/mo", after: "$61k/mo", lift: "+1,425%" },
      { label: "Email captures", before: "18/mo", after: "1,240/mo", lift: "+6,788%" },
      { label: "Lighthouse", before: "48", after: "98", lift: "+50" },
    ],
    testimonial: { author: "Camila R.", role: "Founder", quote: "Yosi turned our Instagram-only shop into a real brand. We hit our first six-figure month within eight weeks of launch." },
  },
  "saas-01": {
    name: "Vertex Business Consulting", industry: "Professional Services",
    hero: "Deployed the Yosi CRM to consolidate 4 legacy tools into one platform.",
    timeline: "5 weeks · Oct – Nov 2025",
    services: ["Branding", "Website Design", "CRM Solutions", "Business Automation"],
    tech: ["Yosi CRM", "Stripe", "Notion API"],
    results: [
      { label: "Tools consolidated", before: "4", after: "1", lift: "$8.4k/yr saved" },
      { label: "Time-to-invoice", before: "5 days", after: "same day", lift: "-100%" },
    ],
    testimonial: { author: "James H.", role: "Managing Partner", quote: "We replaced Salesforce, Basecamp, DocuSign and FreshBooks with one Yosi engagement. Our team is faster and happier." },
  },
  "restaurant-01": {
    name: "Coastal Bistro", industry: "Restaurant",
    hero: "From walk-ins only to a fully-booked reservation system in 6 weeks.",
    timeline: "6 weeks · Aug – Sep 2025",
    services: ["Website Design", "Social Media Marketing", "Photography", "Branding"],
    tech: ["Astro", "OpenTable", "Instagram API"],
    results: [
      { label: "Reservations", before: "18/wk", after: "142/wk", lift: "+689%" },
      { label: "Instagram", before: "1.2k", after: "8.4k", lift: "+600%" },
    ],
    testimonial: { author: "Sarah M.", role: "Owner", quote: "Yosi rebuilt our online presence in 6 weeks and doubled our leads in the first quarter." },
  },
  "clinic-01": {
    name: "Downtown Medical Clinic", industry: "Healthcare",
    hero: "Deployed an AI intake assistant that qualifies patients 24/7.",
    timeline: "10 weeks · Jul – Sep 2025",
    services: ["Website Design", "SEO Optimization", "Artificial Intelligence"],
    tech: ["Next.js", "GPT-4o", "Twilio", "HL7 FHIR"],
    results: [
      { label: "After-hours bookings", before: "0", after: "31/wk", lift: "∞" },
      { label: "SEO traffic", before: "180/mo", after: "4,200/mo", lift: "+2,233%" },
    ],
    testimonial: { author: "Dr. Patel", role: "Medical Director", quote: "The Google Business Profile work paid for itself in the first month. The AI assistant paid for itself in the first week." },
  },
  "retail-01": {
    name: "Anchor Retail Group", industry: "Retail",
    hero: "Full brand refresh + e-commerce across 4 store locations.",
    timeline: "12 weeks · Jun – Aug 2025",
    services: ["Website Design", "Branding", "Promotional Products", "Photography"],
    tech: ["Shopify", "Klaviyo", "Meta Ads"],
    results: [
      { label: "Online revenue", before: "$0", after: "$18k/mo", lift: "new channel" },
      { label: "In-store traffic", before: "baseline", after: "+34%", lift: "+34%" },
    ],
    testimonial: { author: "Elena K.", role: "CEO", quote: "We finally look like the brand we always knew we were. Sales followed within weeks." },
  },
  "realestate-01": {
    name: "Highland Realty Partners", industry: "Real Estate",
    hero: "Elegant listing site plus a drone-photography workflow that closes tours faster.",
    timeline: "7 weeks · May – Jun 2025",
    services: ["Website Design", "Drone Photography", "Video Production", "SEO Optimization"],
    tech: ["Next.js", "Contentful", "Vercel", "MLS Feed API"],
    results: [
      { label: "Tour requests", before: "12/mo", after: "94/mo", lift: "+683%" },
      { label: "Avg. days on market", before: "48", after: "19", lift: "-60%" },
    ],
    testimonial: { author: "Priya S.", role: "Broker", quote: "Buyers ask about the photography before they ask about the property. That never used to happen." },
  },
};

/* ------------------------------------------------------------ PORTFOLIO DETAIL */
export function PortfolioDetail() {
  const { key } = useParams();
  const c = CASES[key];
  const [split, setSplit] = useState(50);
  if (!c) return <Navigate to="/portfolio" replace />;

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-800/60" data-testid={`case-detail-${key}`}>
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-16">
          <Link to="/portfolio" className="text-xs text-blue-400 hover:text-blue-300 uppercase tracking-widest mb-4 inline-block">← All case studies</Link>
          <div className="text-xs uppercase tracking-widest text-blue-400 mb-3">{c.industry}</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold mb-4">{c.name}</h1>
          <p className="text-lg text-slate-300 max-w-3xl leading-relaxed">{c.hero}</p>
          <div className="flex flex-wrap gap-3 mt-6 text-sm text-slate-400">
            <span className="inline-flex items-center gap-1.5"><CalendarIcon className="w-4 h-4 text-blue-400" />{c.timeline}</span>
            <span className="inline-flex items-center gap-1.5"><Briefcase className="w-4 h-4 text-blue-400" />{c.services.length} services delivered</span>
          </div>
        </div>
      </section>

      {/* Before / After slider */}
      <section className="max-w-6xl mx-auto px-6 sm:px-8 py-14" data-testid="case-slider">
        <div className="text-xs uppercase tracking-widest text-blue-400 mb-2">Before / After</div>
        <h2 className="font-display text-3xl font-bold mb-6">Drag the slider.</h2>
        <div className="relative aspect-[16/9] rounded-2xl overflow-hidden border border-slate-800 select-none">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900" />
          <div className="absolute inset-y-0 left-0 bg-gradient-to-br from-blue-500/25 to-cyan-500/15" style={{ width: `${split}%` }} />
          <div className="absolute inset-0 grid grid-cols-2 place-items-center pointer-events-none">
            <div className="text-slate-500 text-xs uppercase tracking-widest">Before</div>
            <div className="text-blue-300 text-xs uppercase tracking-widest">After</div>
          </div>
          <div className="absolute inset-y-0 w-1 bg-blue-400 shadow-lg" style={{ left: `calc(${split}% - 2px)` }} />
          <input type="range" min="0" max="100" value={split} onChange={(e) => setSplit(Number(e.target.value))}
            className="absolute inset-0 opacity-0 cursor-ew-resize w-full h-full" aria-label="Before/After slider" data-testid="case-slider-range" />
        </div>
      </section>

      {/* Services + Tech */}
      <section className="max-w-6xl mx-auto px-6 sm:px-8 py-14 grid grid-cols-1 md:grid-cols-2 gap-10" data-testid="case-meta">
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-400 mb-3">Services delivered</div>
          <div className="flex flex-wrap gap-2">
            {c.services.map((s) => (<span key={s} className="text-xs px-3 py-1.5 rounded-md bg-blue-500/10 border border-blue-500/30 text-blue-300">{s}</span>))}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-400 mb-3">Technologies used</div>
          <div className="flex flex-wrap gap-2">
            {c.tech.map((t) => (<span key={t} className="text-xs px-3 py-1.5 rounded-md bg-slate-800/70 border border-slate-700 text-slate-300 font-mono">{t}</span>))}
          </div>
        </div>
      </section>

      {/* Business results */}
      <section className="bg-slate-900/40 border-y border-slate-800/60 py-14" data-testid="case-results">
        <div className="max-w-6xl mx-auto px-6 sm:px-8">
          <div className="text-xs uppercase tracking-widest text-blue-400 mb-2">Business results</div>
          <h2 className="font-display text-3xl font-bold mb-8">The numbers that matter.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {c.results.map((r) => (
              <div key={r.label} className="rounded-xl border border-slate-800 bg-slate-950/60 p-6">
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">{r.label}</div>
                <div className="flex items-baseline gap-2">
                  <span className="text-slate-500 line-through">{r.before}</span>
                  <ArrowRight className="w-3 h-3 text-slate-600" />
                  <span className="text-2xl font-bold text-blue-400">{r.after}</span>
                </div>
                <div className="text-xs text-emerald-400 font-semibold mt-2">{r.lift}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="max-w-4xl mx-auto px-6 sm:px-8 py-14" data-testid="case-testimonial">
        <div className="rounded-2xl border border-blue-500/30 bg-blue-500/5 p-10">
          <Quote className="w-10 h-10 text-blue-400 mb-4" />
          <p className="text-xl text-slate-100 leading-relaxed mb-6 italic">"{c.testimonial.quote}"</p>
          <div className="text-sm text-slate-100 font-semibold">{c.testimonial.author}</div>
          <div className="text-xs text-slate-500">{c.testimonial.role}, {c.name}</div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto text-center px-6 pb-20" data-testid="case-cta">
        <h2 className="font-display text-3xl font-bold mb-4">Want results like these?</h2>
        <Link to="/contact" className="btn-scale h-12 px-6 rounded-lg bg-blue-500 hover:bg-blue-400 text-white font-semibold text-sm inline-flex items-center gap-2 shadow-xl shadow-blue-500/40">
          Start Your Project <ArrowRight className="w-4 h-4" />
        </Link>
      </section>
    </MarketingLayout>
  );
}

/* ------------------------------------------------------------ TESTIMONIAL CAROUSEL (dynamic — admin-managed) */
export function TestimonialsCarousel() {
  const { lang, t: tr } = useLang();
  const [rows, setRows] = useState(null); // null = loading, [] = empty
  const [i, setI] = useState(0);

  useEffect(() => {
    setRows(null);
    setI(0);
    api.get("/public/testimonials", { params: { lang } })
      .then(({ data }) => setRows(Array.isArray(data) ? data : []))
      .catch(() => setRows([]));
  }, [lang]);

  const next = useCallback(() => {
    setI((v) => (rows && rows.length ? (v + 1) % rows.length : 0));
  }, [rows]);
  const prev = useCallback(() => {
    setI((v) => (rows && rows.length ? (v - 1 + rows.length) % rows.length : 0));
  }, [rows]);
  useEffect(() => {
    if (!rows || rows.length < 2) return;
    const t = setInterval(next, 7000);
    return () => clearInterval(t);
  }, [next, rows]);

  // Loading — render nothing to avoid layout flash
  if (rows === null) return null;
  // Empty state — the entire section is hidden per requirement
  if (rows.length === 0) return null;

  const t = rows[i] || rows[0];
  const showStars = t.rating != null && t.source !== "instagram";

  return (
    <section className="bg-slate-900/40 border-y border-slate-800/60 py-16" data-testid="testimonials-carousel">
      <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
        <div className="text-xs uppercase tracking-widest text-blue-400 mb-4">{tr("public.testimonials.over")}</div>
        {showStars && (
          <div className="flex gap-1 justify-center mb-6" data-testid="testimonial-stars">
            {[1,2,3,4,5].map((n) => (
              <Star key={n} className={`w-5 h-5 ${n <= Math.round(t.rating) ? "text-blue-400 fill-blue-400" : "text-slate-700"}`} />
            ))}
          </div>
        )}
        <p className="text-2xl sm:text-3xl font-display leading-relaxed mb-8 min-h-[6rem]">"{t.review_text}"</p>
        <div className="flex items-center justify-center gap-3 mb-2">
          {t.client_photo_url && (
            <img src={t.client_photo_url} alt={t.client_name}
                 className="w-10 h-10 rounded-full object-cover border border-slate-700" />
          )}
          <div>
            <div className="text-sm font-semibold text-slate-100">{t.client_name}</div>
            {t.company_name && <div className="text-xs text-slate-500">{t.company_name}</div>}
          </div>
        </div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-6">
          via {(t.source || "direct").replace("_", " ")}
          {t.review_date && <> · {t.review_date}</>}
        </div>
        {t.source_url && (
          <a href={t.source_url} target="_blank" rel="noopener noreferrer"
             data-testid="testimonial-view-original"
             className="btn-scale inline-flex items-center gap-2 h-9 px-4 rounded-full border border-blue-500/40 text-blue-300 text-xs font-semibold hover:border-blue-400 hover:bg-blue-500/10 mb-6">
            {tr("public.testimonials.view_original")} <ExternalLink className="w-3 h-3" />
          </a>
        )}
        {rows.length > 1 && (
          <div className="flex items-center justify-center gap-4">
            <button onClick={prev} data-testid="testimonial-prev" className="btn-scale w-10 h-10 grid place-items-center rounded-full border border-slate-700 text-slate-300 hover:bg-slate-800"><ChevronLeft className="w-4 h-4" /></button>
            <div className="flex gap-1.5">{rows.map((_, idx) => (<button key={idx} onClick={() => setI(idx)} className={`w-2 h-2 rounded-full transition-colors ${idx === i ? "bg-blue-400 w-6" : "bg-slate-700"}`} aria-label={`Go to slide ${idx+1}`} />))}</div>
            <button onClick={next} data-testid="testimonial-next" className="btn-scale w-10 h-10 grid place-items-center rounded-full border border-slate-700 text-slate-300 hover:bg-slate-800"><ChevronRight className="w-4 h-4" /></button>
          </div>
        )}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------ TRUST SECTION */
export function TrustSection() {
  const stats = [
    { Icon: Award, n: "8+", l: "Years of experience" },
    { Icon: Briefcase, n: "150+", l: "Projects completed" },
    { Icon: Users, n: "98%", l: "Happy clients" },
    { Icon: Star, n: "4.9/5", l: "Google Reviews" },
    { Icon: Building2, n: "22", l: "Industries served" },
  ];
  return (
    <section className="max-w-7xl mx-auto px-6 sm:px-8 py-16" data-testid="trust-section">
      <div className="text-xs uppercase tracking-widest text-blue-400 mb-2 text-center">Trusted at scale</div>
      <h2 className="font-display text-3xl sm:text-4xl font-bold text-center mb-10">Businesses that grew with us.</h2>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {stats.map(({ Icon, n, l }) => (
          <div key={l} className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 text-center">
            <Icon className="w-6 h-6 text-blue-400 mx-auto mb-3" />
            <div className="text-3xl font-bold text-slate-100">{n}</div>
            <div className="text-xs text-slate-500 mt-1">{l}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------ BLOG */
const POSTS = [
  { slug: "why-your-website-fails", title: "Why your website is losing 90% of visitors — and how to fix it", excerpt: "Most business websites optimise for looks, not for conversion. Here's what to change first.", category: "Websites", date: "2026-01-18", featured: true, read: "6 min" },
  { slug: "seo-in-2026", title: "SEO in 2026: AI Overviews, EEAT and the death of keyword stuffing", excerpt: "The rules changed. Here's how to rank when AI writes the first answer.", category: "SEO", date: "2026-01-11", read: "8 min" },
  { slug: "google-business-playbook", title: "The Google Business Profile playbook local businesses actually need", excerpt: "12 tactics that move the map-pack ranking in 30 days.", category: "SEO", date: "2026-01-04", read: "9 min" },
  { slug: "brand-refresh-vs-rebrand", title: "Brand refresh vs. rebrand — which one does your business need?", excerpt: "A framework we use with every client considering brand work.", category: "Branding", date: "2025-12-20", read: "5 min" },
  { slug: "ai-support-agent", title: "Deploying an AI customer-support agent in 2 weeks", excerpt: "The exact process we use — from scoping to launch to KPI tracking.", category: "AI", date: "2025-12-14", read: "10 min" },
  { slug: "nfc-card-roi", title: "NFC business cards ROI: real numbers from 40 clients", excerpt: "How much extra pipeline a $12 card actually generates.", category: "NFC", date: "2025-12-05", read: "4 min" },
];

export function Blog() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const cats = ["All", ...Array.from(new Set(POSTS.map((p) => p.category)))];
  const filtered = POSTS.filter((p) => (!cat || cat === "All" || p.category === cat) && (!q || (p.title + p.excerpt).toLowerCase().includes(q.toLowerCase())));
  const featured = POSTS.find((p) => p.featured);

  return (
    <MarketingLayout>
      <section className="max-w-6xl mx-auto px-6 sm:px-8 py-16" data-testid="blog-page">
        <div className="text-xs uppercase tracking-widest text-blue-400 mb-2">Blog</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold mb-4">Insights from the studio.</h1>
        <p className="text-slate-400 max-w-2xl mb-10">Playbooks, case studies and hard-won lessons from the front lines of digital growth.</p>

        <div className="flex flex-wrap gap-3 mb-8">
          <div className="relative flex-1 min-w-[240px]">
            <SearchIcon className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles…" data-testid="blog-search" className="w-full h-10 pl-9 pr-3 bg-slate-950 border border-slate-800 rounded-md text-sm text-slate-200 focus:border-blue-500 outline-none" />
          </div>
          <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="blog-category" className="h-10 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm text-slate-200">
            {cats.map((c) => <option key={c} value={c === "All" ? "" : c}>{c}</option>)}
          </select>
        </div>

        {featured && !q && !cat && (
          <Link to={`/blog/${featured.slug}`} className="block rounded-2xl border border-blue-500/40 bg-blue-500/5 p-8 mb-10 hover:border-blue-500 transition-colors" data-testid="blog-featured">
            <span className="text-[10px] uppercase tracking-widest text-blue-300 bg-blue-500/20 px-2 py-0.5 rounded">Featured</span>
            <h2 className="font-display text-2xl sm:text-3xl font-bold mt-3 mb-3">{featured.title}</h2>
            <p className="text-slate-300 mb-4">{featured.excerpt}</p>
            <div className="text-xs text-slate-500">{featured.category} · {featured.date} · {featured.read} read</div>
          </Link>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((p) => (
            <Link key={p.slug} to={`/blog/${p.slug}`} className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 hover:border-blue-500/40 transition-colors" data-testid={`blog-post-${p.slug}`}>
              <div className="text-xs text-blue-400 uppercase tracking-widest mb-2">{p.category}</div>
              <h3 className="font-display font-semibold text-lg mb-2 leading-snug">{p.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed mb-3 line-clamp-2">{p.excerpt}</p>
              <div className="text-xs text-slate-500">{p.date} · {p.read} read</div>
            </Link>
          ))}
          {filtered.length === 0 && <div className="col-span-full text-center text-slate-500 py-12">No articles match.</div>}
        </div>
      </section>
    </MarketingLayout>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const post = POSTS.find((p) => p.slug === slug);
  if (!post) return <Navigate to="/blog" replace />;
  const related = POSTS.filter((p) => p.slug !== slug && p.category === post.category).slice(0, 3);
  return (
    <MarketingLayout>
      <article className="max-w-3xl mx-auto px-6 sm:px-8 py-16" data-testid={`blog-post-page-${slug}`}>
        <Link to="/blog" className="text-xs text-blue-400 hover:text-blue-300 uppercase tracking-widest mb-4 inline-block">← Blog</Link>
        <div className="text-xs uppercase tracking-widest text-blue-400 mb-3">{post.category}</div>
        <h1 className="font-display text-4xl font-bold leading-tight mb-4">{post.title}</h1>
        <div className="text-sm text-slate-500 mb-8">{post.date} · {post.read} read</div>
        <div className="prose prose-invert prose-slate max-w-none text-slate-300 leading-relaxed space-y-4">
          <p className="text-lg">{post.excerpt}</p>
          <p>Full article content forthcoming. This scaffold reserves the URL, sitemap slot, and internal linking. Contact <Link to="/contact" className="text-blue-400">our team</Link> for the full playbook until the article is published.</p>
        </div>
      </article>
      {related.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 sm:px-8 pb-16" data-testid="blog-related">
          <h2 className="font-display text-2xl font-bold mb-6">Related articles</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {related.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 hover:border-blue-500/40 transition-colors">
                <div className="text-xs text-blue-400 uppercase tracking-widest mb-2">{p.category}</div>
                <h3 className="font-display font-semibold text-lg leading-snug">{p.title}</h3>
              </Link>
            ))}
          </div>
        </section>
      )}
    </MarketingLayout>
  );
}

/* ------------------------------------------------------------ CAREERS */
const JOBS = [
  { key: "senior-fullstack", title: "Senior Full-stack Engineer", team: "Engineering", loc: "Remote (Americas)", type: "Full-time" },
  { key: "brand-designer", title: "Senior Brand Designer", team: "Design", loc: "Remote (Americas)", type: "Full-time" },
  { key: "seo-strategist", title: "SEO Strategist", team: "Marketing", loc: "Remote (Americas)", type: "Full-time" },
  { key: "video-producer", title: "Video Producer & Editor", team: "Content", loc: "Hybrid — Alabama, USA", type: "Full-time" },
];

export function Careers() {
  const [form, setForm] = useState({ name: "", email: "", role: "", message: "" });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post("/contact", {
        name: form.name, email: form.email, message: form.message || `Applying for ${form.role}`,
        service_needed: `Careers: ${form.role}`, source: "careers_application",
      });
      toast.success("Application received!"); setSent(true);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit"); }
    finally { setSubmitting(false); }
  };
  return (
    <MarketingLayout>
      <section className="max-w-5xl mx-auto px-6 sm:px-8 py-16" data-testid="careers-page">
        <div className="text-xs uppercase tracking-widest text-blue-400 mb-2">Careers</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold mb-4">Build the future of digital agencies.</h1>
        <p className="text-slate-400 max-w-2xl mb-10">We hire senior specialists who care deeply about craft. If that's you, we'd love to talk.</p>

        <h2 className="font-display text-2xl font-bold mb-6">Open positions</h2>
        <div className="space-y-3 mb-14">
          {JOBS.map((j) => (
            <div key={j.key} className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`job-${j.key}`}>
              <div>
                <div className="font-display font-semibold text-lg text-slate-100">{j.title}</div>
                <div className="text-xs text-slate-500 mt-1">{j.team} · {j.loc} · {j.type}</div>
              </div>
              <button onClick={() => setForm((f) => ({ ...f, role: j.title }))} data-testid={`job-apply-${j.key}`} className="btn-scale h-9 px-4 rounded-md bg-blue-500 hover:bg-blue-400 text-white text-sm font-semibold">Apply</button>
            </div>
          ))}
        </div>

        {sent ? (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-8 text-center" data-testid="careers-success">
            <div className="text-lg font-semibold text-slate-100 mb-2">Application received!</div>
            <p className="text-slate-400 text-sm">We review every application within 5 business days.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-xl border border-slate-800 bg-slate-900/40 p-6 space-y-4 max-w-2xl" data-testid="careers-form">
            <h3 className="font-display font-semibold text-lg">Apply now</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="careers-name" className="h-10 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm outline-none focus:border-blue-500" />
              <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="careers-email" className="h-10 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm outline-none focus:border-blue-500" />
            </div>
            <select required value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="careers-role" className="w-full h-10 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm outline-none focus:border-blue-500">
              <option value="">— Choose a role —</option>
              {JOBS.map((j) => <option key={j.key} value={j.title}>{j.title}</option>)}
            </select>
            <textarea rows={4} placeholder="Tell us about yourself + link to portfolio / GitHub / LinkedIn" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="careers-message" className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm outline-none focus:border-blue-500" />
            <button type="submit" data-testid="careers-submit" className="btn-scale h-10 px-6 rounded-md bg-blue-500 hover:bg-blue-400 text-white font-semibold text-sm shadow-lg shadow-blue-500/40">Submit application</button>
          </form>
        )}
      </section>
    </MarketingLayout>
  );
}

/* ------------------------------------------------------------ CLIENT SUCCESS */
export function ClientSuccess() {
  return (
    <MarketingLayout>
      <section className="max-w-6xl mx-auto px-6 sm:px-8 py-16" data-testid="success-page">
        <div className="text-xs uppercase tracking-widest text-blue-400 mb-2">Client Success</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold mb-4">How businesses grew after working with Yosi.</h1>
        <p className="text-slate-400 max-w-2xl mb-12">Real stories. Real numbers. Every metric here is from a live client engagement.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Object.entries(CASES).map(([k, c]) => (
            <Link key={k} to={`/portfolio/${k}`} className="rounded-xl border border-slate-800 bg-slate-900/40 p-6 hover:border-blue-500/40 transition-all group" data-testid={`success-${k}`}>
              <div className="text-xs text-blue-400 uppercase tracking-widest mb-2">{c.industry}</div>
              <h3 className="font-display text-xl font-bold mb-2 text-slate-100">{c.name}</h3>
              <p className="text-sm text-slate-300 mb-4 leading-relaxed">{c.hero}</p>
              <div className="grid grid-cols-2 gap-3 mb-4">
                {c.results.slice(0, 2).map((r) => (
                  <div key={r.label} className="rounded-md bg-slate-950/70 p-3 border border-slate-800">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">{r.label}</div>
                    <div className="text-lg font-bold text-blue-400">{r.lift}</div>
                  </div>
                ))}
              </div>
              <div className="text-xs text-slate-500 italic border-l-2 border-blue-500/40 pl-3">"{c.testimonial.quote.slice(0, 120)}…"</div>
            </Link>
          ))}
        </div>
      </section>
    </MarketingLayout>
  );
}
